
import axios from 'axios';

import Vue from 'vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import _pickBy from 'lodash.pickby';
import format from 'string-format';
import { trackEvent } from '@/util/tracking';
import { Polygon, AllGeoJSON, Position } from '@turf/helpers';
import {
  buildFeature,
  createChildFeatures,
  mapPolygonsToRedirectURL,
  mapSurveyDataToRedirectURL,
  upsertParentWithChild,
} from '@/util/features';
import {
  ContentBlockConfig,
  ContentBlockTypes,
  Feature,
  GeocodeResult,
  InputBlockConfig,
  isFailedResponse,
  isInputBlockConfig,
  NullableGeometry,
  ParentChildPair,
  RedirectionConfig,
  SubmissionConfig,
  VetroResponse,
} from '@/types';
import turfCentroid from '@turf/centroid';
import { failureAlert } from '@/util/alert';
import { pointFromPosition } from '@/util/geom';
import { getMatchingIntersectionRelationshipPolygons } from '@/util/polygonIntersections';
import { PolygonIntersectionIdentifier } from '@/types/polygonIntersections';
import CloseButton from '@/components/util/CloseButton.vue';
import PaginationHeader from '../../util/PaginationHeader.vue';
import { submitFile } from '../../util/SubmitFile';
import SidebarBlockGroup from './SidebarBlockGroup.vue';
import CommentFeed from './comments/CommentFeed.vue';
import { IndexActions, SELECTION_TYPE } from '../../../constants';

interface SidebarData {
  termsAreAccepted: boolean;
  submitting: boolean;
  chosenParentFeature: Feature | null;
  IndexActions: typeof IndexActions;
}

export default Vue.extend({
  name: 'sidebar-content',
  components: { SidebarBlockGroup, CommentFeed, CloseButton, PaginationHeader },
  props: {
    addAddressManually: { type: Boolean, required: true },
  },
  data(): SidebarData {
    return {
      termsAreAccepted: false,
      submitting: false,
      chosenParentFeature: null,
      IndexActions,
    };
  },
  computed: {
    ...mapGetters('config', [
      'getViewAttributes',
      'isFeatureSelectionEnabled',
      'commentableLayerIds',
      'parentlessCommentsEnabled',
      'allowCommentOnSelectedFeature',
    ]),
    ...mapGetters('sidebar', ['getSubmissionRedirectUrl', 'getFormValidity']),
    ...mapGetters('comments', ['commentParentPairs']),
    ...mapGetters('user', ['userInfo']),
    ...mapState('polygonIntersections', [
      'polygonIntersectionRelationships',
      'isLoadingPolygonIntersectionRelationships',
    ]),
    ...mapState('sidebar', ['surveyData', 'formInErrorState', 'paginationIndex']),
    ...mapState('features', ['selectedFeatures', 'featuresAtFocusPoint', 'surveyEnabledSelection']),
    ...mapState('config', ['viewAttributes', 'readOnlyMode', 'readOnlyMessage']),
    ...mapState('map', ['activeLocation']),
    showSubmitButton(): boolean {
      return (
        this.surveyEnabledSelection ||
        this.viewAttributes?.selectionType === SELECTION_TYPE.ADDRESS_SEARCH
      );
    },
    parentFeature(): Feature {
      // chosenParentFeature becomes the parent when parentless commenting is enabled
      // and the user has chosen a feature from the parent select dropdown in the survey.
      return this.selectedFeatures[this.paginationIndex] || this.chosenParentFeature;
    },
    filteredCommentParentPairs(): ParentChildPair[] {
      return this.commentParentPairs.filter(
        (p: ParentChildPair) => p.child.xVetro.parentVetroId === this.parentFeature.xVetro.vetroId,
      );
    },
    shouldShowForm(): boolean {
      if (!this.isFeatureSelectionEnabled) return true;
      if (!this.surveyEnabledSelection) return false;

      return this.selectedFeatures.length > 0
        ? this.selectedFeatures.some((feature: Feature) =>
            this.commentableLayerIds.has(feature.xVetro?.layerId),
          )
        : true;
    },
    sidebarContentBlocks(): ContentBlockConfig[] {
      return (this.getViewAttributes?.sidebarConfig?.contentBlocks || []).filter(
        (block: ContentBlockConfig) =>
          this.shouldShowForm || block.type === ContentBlockTypes.FeatureInfo,
      );
    },
    hideTermsCheckbox(): boolean {
      return this.getViewAttributes?.sidebarConfig?.hideTermsCheckbox || false;
    },
    submissionConfig(): SubmissionConfig | null {
      return this.getViewAttributes?.submissionConfig || null;
    },
    submissionRedirectUrl(): string | null {
      return this.getSubmissionRedirectUrl || null;
    },
    inputBlockConfigs(): InputBlockConfig[] {
      return this.sidebarContentBlocks.filter(isInputBlockConfig);
    },
    allFormComponentsValid(): boolean {
      return Object.values(this.getFormValidity).every(Boolean);
      // return !allFormComponentsValid && this.formInErrorState;
    },
    submissionEnabled(): boolean {
      // FormValidity is an object with only booleans as values. Check if any are false!
      // const allFormComponentsValid = Object.values(this.getFormValidity).every(Boolean);
      const isShowingErrors = !this.allFormComponentsValid && this.formInErrorState;
      return (
        !this.formDisabled &&
        !isShowingErrors &&
        (this.termsAreAccepted || this.hideTermsCheckbox) &&
        !this.submitting
      );
    },
    redirectConfig(): RedirectionConfig | undefined {
      return this.submissionConfig?.redirect;
    },
    skipSubmissionModal(): boolean {
      return this.submissionConfig?.redirect?.skipSubmissionModal ?? false;
    },
    intersectingPolygons(): Feature<Polygon>[] {
      const redirectPolygonIntersectionIdentifier: PolygonIntersectionIdentifier | undefined =
        this.redirectConfig?.polygonIntersectionMapping;

      if (redirectPolygonIntersectionIdentifier) {
        const polygons = getMatchingIntersectionRelationshipPolygons(
          redirectPolygonIntersectionIdentifier,
          this.polygonIntersectionRelationships,
        );
        return polygons;
      }
      return [];
    },
    formDisabled(): boolean {
      return this.readOnlyMode;
    },
    formDisabledReason(): string {
      return this.readOnlyMessage;
    },
    reasonSubmissionDisabled() {
      if (this.formDisabled) {
        return this.formDisabledReason;
      }
      if (!this.allFormComponentsValid) {
        return 'Please correct errors in form';
      }
      if (!this.termsAreAccepted) {
        return 'Please review the Terms of Service and Privacy Policy';
      }
      if (this.submitting) {
        return 'Currently submitting';
      }
      return 'Not Disabled';
    },
  },
  watch: {
    activeLocation(location: Position) {
      if (location) this.chosenParentFeature = null;
    },
    selectedFeatures() {
      if (
        this.selectedFeatures.length === 0 ||
        this.selectedFeatures.length <= this.paginationIndex
      ) {
        if (this.selectedFeatures.length === 0) {
          this.resetSidebarState();
        }
        // A guard against any weird states that the sidebar can get into by accident
        this.handleIndex({
          indexAction: IndexActions.CLEAR,
          selectedFeatures: this.selectedFeatures.length,
        });
      }
    },
  },
  methods: {
    ...mapActions('sidebar', [
      'setSubmissionRedirectUrl',
      'setFormErrorState',
      'setSurveyActive',
      'handleIndex',
      'resetSidebarState',
    ]),
    ...mapActions('config', ['resetState']),
    ...mapActions('features', ['resetFeatureState']),
    ...mapActions('comments', ['fetchCommentsInPolygon']),
    ...mapActions('map', ['resetActiveLocation']),
    ...mapActions('error', ['setCriticalError']),
    /**
     * In this function, we do the following if configured:
     *
     * - attach SL or Customer feature properties as query params to the redirect URL
     * - select a different redirect URL based on zone status, falling
     *   back to the baseRedirectUrl if none are configured for the
     *   user's current zone status(es)
     *
     * (Though Shared Views now supports more parents/children than just serviceLocation
     * and customer, custom ZM manager redirects currently rely on those naming
     * conventions.)
     */
    buildSubmissionRedirectUrl(serviceLocationAndCustomer: {
      serviceLocation: Feature | null;
      customer: Feature;
    }) {
      let url;

      if (
        this.redirectConfig?.polygonIntersectionMapping &&
        this.intersectingPolygons?.length &&
        this.intersectingPolygons.length > 0
      ) {
        url = mapPolygonsToRedirectURL(this.intersectingPolygons, this.redirectConfig);
      } else if (this.redirectConfig?.formDataIntersectionMapping) {
        url = mapSurveyDataToRedirectURL(this.surveyData, this.redirectConfig);
      } else if (this.redirectConfig?.url) {
        url = this.redirectConfig.url;
      } else {
        url = '';
      }

      /**
       * Here we attach data from the Service Location or Customer features,
       * if the redirect URL contains placeholder(s) like:
       * https://zm-customer-site.com/search?customer={serviceLocation.xVetro.vetroId}
       */
      const formattedUrl = format(url, serviceLocationAndCustomer);
      this.setSubmissionRedirectUrl(formattedUrl);
    },
    async submit() {
      const allFormComponentsValid = Object.values(this.getFormValidity).every(Boolean);
      if (!allFormComponentsValid) {
        this.setFormErrorState(true);
        failureAlert(this.$store, 'Please review your submission and try again.');
        trackEvent('shared_view_submission_failed', { error: 'Form invalid' });
        return;
      }
      this.submitting = true;
      try {
        const { submissionConfig, typeaheadAddressAttribute: addressAttribute = 'Address' } =
          this.viewAttributes;

        if (!submissionConfig) {
          // eslint-disable-next-line no-console
          console.error('No submission_config is configured on the view.');
          trackEvent('shared_view_submission_failed', {
            error: 'No submission_config configured',
          });
        }

        const childCreationPayload = {
          properties: _pickBy(this.surveyData, (value) => !!value),
          submissionConfig,
          addressAttribute,
        };

        // Files are uploaded here, and then the file attributes are set to
        // an array with the associated s3 key and label e.g.
        // {"fileAttribute": [{"key": "abc", label: "file.zip" }]}
        const fileUploadPromises = Object.entries(childCreationPayload.properties)
          .filter(([__, value]) => value instanceof File)
          .map(submitFile);
        const fileAttributeValues = await Promise.all(fileUploadPromises);
        const updatedFileProperties = fileAttributeValues.reduce((acc, value) => {
          if (!value) return acc;
          return { ...acc, ...value };
        }, {});

        childCreationPayload.properties = {
          ...childCreationPayload.properties,
          ...updatedFileProperties,
        };

        let firstParent: Feature | null;
        let child: Feature;

        const shouldUpsert =
          !this.chosenParentFeature &&
          !this.selectedFeatures[this.paginationIndex]?.xVetro?.vetroId;
        // this means either there is a stub feature, or an address wasn't found

        if (shouldUpsert) {
          // Upsert is only used in address search mode, where only one feature can be selected

          let parent = this.selectedFeatures[this.paginationIndex];
          if (!parent) {
            const {
              'Street Address': streetAddress,
              Unit,
              City,
              State,
              'Zip Code': zipCode,
            } = childCreationPayload.properties;

            const address = [streetAddress, Unit, City, [State, zipCode].join(' ')]
              .filter(Boolean)
              .join(', ');
            if (![streetAddress, City, State, zipCode].every(Boolean)) {
              const error = new Error(
                `Missing values to create address ${JSON.stringify({
                  streetAddress,
                  City,
                  State,
                  zipCode,
                })}`,
              );
              this.setCriticalError(error);
              throw error;
            }
            const { data: response }: { data: VetroResponse<GeocodeResult> } = await axios.get(
              `/v2/address/geocode/${address}`,
            );

            if (isFailedResponse(response)) {
              const error = new Error(`Failed to geocode address '${address}'`);
              this.setCriticalError(error);
              throw error;
            }
            const { geometry } = response.result;

            const properties = {
              Address: address,
              'Street Address': streetAddress,
              Unit,
              City,
              State,
              'Zip Code': zipCode,
            };

            parent = buildFeature({
              geometry: geometry as NullableGeometry,
              properties,
              layerId: this.viewAttributes.submissionConfig.parentLayerId,
              planId: this.viewAttributes.submissionConfig.submissionPlanId,
            });
          }

          [firstParent, child] = await upsertParentWithChild({
            ...childCreationPayload,
            parent,
          });
        } else {
          let parents: Feature[];
          if (this.selectedFeatures?.length > 0) {
            parents = this.selectedFeatures;
          } else if (this.chosenParentFeature) {
            parents = [this.chosenParentFeature];
          } else {
            parents = [];
          }
          [firstParent] = parents;
          let parentsWithChildGeoms;
          parentsWithChildGeoms = parents
            .filter((parent) => this.commentableLayerIds.has(parent.xVetro?.layerId))
            .map((parent) => {
              if (!this.isFeatureSelectionEnabled) return { parent, childGeom: null };
              const childGeom = parent
                ? turfCentroid(parent as unknown as AllGeoJSON).geometry
                : pointFromPosition(this.activeLocation);
              return { parent, childGeom };
            });

          // Handle case where no parent is selected, but we still want to create a child feature:
          if (!parentsWithChildGeoms || parentsWithChildGeoms.length < 1) {
            parentsWithChildGeoms = [
              {
                parent: null,
                childGeom: pointFromPosition(this.activeLocation),
              },
            ];
          }

          child = await createChildFeatures({
            ...childCreationPayload,
            parentsWithChildGeoms,
          });
        }

        this.buildSubmissionRedirectUrl({ serviceLocation: firstParent, customer: child });

        if (this.skipSubmissionModal) {
          if (this.submissionRedirectUrl) {
            window.location.href = this.submissionRedirectUrl;
          } else {
            this.resetState();
          }
        } else {
          this.$bvModal.show('submission-modal');
          this.resetState();
        }
      } catch (error) {
        trackEvent('shared_view_submission_failed', { error: error.message });
        failureAlert(this.$store, 'Survey submission failed.');
      }

      trackEvent('shared_view_submission_successful');
      this.submitting = false;
    },
    closeForm() {
      this.addAddressManually = false;
      this.setSurveyActive(false);
      this.resetActiveLocation();
      this.resetFeatureState();
      this.resetSidebarState();
    },
  },
});
